import React, { useEffect, useMemo, useState } from "react";
import ApiData from "../../../api/ApiData";
import RolesState from "../../../api/RolesState";
import User from "../../../api/User";
import { DashCard } from "../../UI/DashCard";
import { NoRightsMessage } from "../../UI/NoRightsMessage";
import { BtnPrimaryv4 } from "../../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../../UI/button/BtnOutlineV4";
import {
  canViewEventTotals,
  EVENT_TOTALS_KEYS,
  EVENT_TOTALS_LABELS,
  formatEventTotalDisplay,
  getCalendarMonthRangeToToday,
  getHalfYearRangeToToday,
  inclusiveDayCount,
  normalizeTotals,
  pickCompanyTotals,
  totalsToAvgPerDay,
} from "../../../utils/eventTotalsSaUtils";
import { formatIsoDateShortDisplay } from "../../../utils/subscriptionPeriodDate";

const PERIOD_MODES = [
  { id: "avgMonth", label: "Ср. в день за месяц" },
  { id: "month", label: "Месяц" },
  { id: "halfYear", label: "Полгода" },
];

function resolveRange(mode) {
  if (mode === "halfYear") {
    return getHalfYearRangeToToday();
  }
  return getCalendarMonthRangeToToday();
}

function valuesForMode(totals, mode, from, to) {
  const normalized = normalizeTotals(totals);
  if (mode === "avgMonth") {
    return totalsToAvgPerDay(normalized, from, to);
  }
  return normalized;
}

export function CompanyEventTotalsBlock({ companyId }) {
  const canView = canViewEventTotals(RolesState, User.user);
  const [mode, setMode] = useState("month");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [totals, setTotals] = useState(null);

  const range = useMemo(() => resolveRange(mode), [mode]);

  useEffect(() => {
    if (!canView || companyId == null) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      const res = await ApiData.fetchEventTotalsSa({
        filterdate: [range.from, range.to],
        companyIds: [companyId],
      });
      if (cancelled) {
        return;
      }
      if (!res) {
        setError(true);
        setTotals(null);
      } else {
        setTotals(pickCompanyTotals(res.companies, companyId));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, companyId, range.from, range.to]);

  if (!canView) {
    return (
      <DashCard className="dash-card-event-totals" title="Статистика событий">
        <NoRightsMessage>Нет прав для просмотра статистики событий</NoRightsMessage>
      </DashCard>
    );
  }

  const display =
    totals != null
      ? valuesForMode(
          totals,
          mode,
          range.from,
          range.to,
        )
      : null;
  const daysHint =
    mode === "avgMonth"
      ? `за ${inclusiveDayCount(range.from, range.to)} дн.`
      : `${formatIsoDateShortDisplay(range.from)} — ${formatIsoDateShortDisplay(range.to)}`;

  return (
    <DashCard className="dash-card-event-totals" title="Статистика событий">
      <div
        className="event-totals-period-actions"
        role="group"
        aria-label="Период статистики"
      >
        {PERIOD_MODES.map((item) =>
          mode === item.id ? (
            <BtnPrimaryv4
              key={item.id}
              text={item.label}
              onClick={() => setMode(item.id)}
            />
          ) : (
            <BtnOutlineV4
              key={item.id}
              className="sub-status-comment-btn"
              text={item.label}
              onClick={() => setMode(item.id)}
            />
          ),
        )}
      </div>
      <p className="event-totals-period-hint">{daysHint}</p>
      {loading ? (
        <div
          className="event-totals-skeleton"
          aria-busy="true"
          aria-label="Загрузка статистики"
        >
          <div className="event-totals-skeleton-chip" />
          <div className="event-totals-skeleton-chip" />
          <div className="event-totals-skeleton-chip" />
          <div className="event-totals-skeleton-chip" />
        </div>
      ) : error ? (
        <p className="event-totals-muted">Не удалось загрузить статистику</p>
      ) : (
        <div className="metrics-chips event-totals-metrics">
          {EVENT_TOTALS_KEYS.map((key) => (
            <div key={key} className="chip chip-total">
              <div className="chip-val">
                {formatEventTotalDisplay(display?.[key])}
              </div>
              <div className="chip-lbl">{EVENT_TOTALS_LABELS[key]}</div>
            </div>
          ))}
        </div>
      )}
    </DashCard>
  );
}
